import React, { useContext, useEffect, useState } from "react";
import { Button, Row, Col, Table, Modal, Input, Checkbox, Form, Select, Spin, Drawer } from "antd";
import { withRouter } from "react-router-dom";
import SectionCard from "./SectionCard";
import "./Adjustments.scss";
import { ChevrondownIcon, ChevronUpIcon, DeleteIconAdjustments, EditIconSvg, SelectDropDownIcon } from "../../common/CustomIcon";
import { OrgContext } from "../../context/OrgContext";
import { deleteAdjustment, getAdjustments, saveAddAdjustment, saveEditAdjustment } from "../../api/Billing";
import { createOrderQuoteAmount } from "../../api/OrdersApi";
import { renderNotification } from "../../components/orders/OrderTypeIcons";
import { QuotesApis } from "../../api/QuotesApi";
import { checkAccessExistance, renderAlertMessage } from "../../helpers/common";
import { alertMessage, checkNegitive, isEmpty, randomNumber } from "../../common/Common";
import moment from "moment";
import AppConfig from "../../config/AppConfig";
import GLCodeSelect from "../../components/common/SelectDropdowns/GLCodeSelect";
import I18n from "../../common/I18n";
import OrderDetailsDrawer from "./OrderDetailsDrawer";

const Adjustments = (props) => {
  const { orderResponse, form , refreshDetails,account, setEstimateRefreshKey = () => {}} = props;
  const canEdit = checkAccessExistance("ESTIMATE_EDIT");
  const [editingCharges, setEditingCharges] = useState({});
  const [isEditingAdjustment, setIsEditingAdjustment] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const { glCodesList = [] } = useContext(OrgContext);
  const [collapsed, setCollapsed] = useState(false);
  const locations = orderResponse?.locations || []
  const { getFieldDecorator, validateFields } = form;
  const [adjustmentData, setAdjustmentData] = useState([]);
  const [rateDetails, setRateDetails] = useState({
    PICKUP: { distance: "", rate: "", includesFuel: false, name: "", flat_fuel_amount: "" },
    DELIVERY: { distance: "", rate: "", includesFuel: false, name: "", flat_fuel_amount: "" },
  });
  const [cancelRateDetails, setCancelRateDetails] = useState({
    PICKUP: { distance: "", rate: "", includesFuel: false, name: "", flat_fuel_amount: "" },
    DELIVERY: { distance: "", rate: "", includesFuel: false, name: "", flat_fuel_amount: "" },
  });

  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [selectedLocationType, setSelectedLocationType] = useState(null);
  const [currentQuote, setCurrentQuote] = useState(null);
  const [loading , setLoading]= useState(false);
  const [adjustmentLoading , setAdjustmentLoading]= useState(false);
  const [showDrawer, setShowDrawer] = useState(false)
  const [viewTab, setViewTab] = useState("")

  // Check if we need to add linehaul section
  const orderType = orderResponse?.customer_order_details?.type_of_order;
  // New LH labels (PLH/LHD/PLHD/NLH) send their billed legs; old auto LH uses is_lh_order
  const chargeLegs = orderResponse?.customer_order_details?.charge_leg_types;
  const shouldShowLinehaul = orderResponse?.customer_order_details?.is_lh_order || chargeLegs?.includes("LINEHAUL") || false;
  const chargeLocations = chargeLegs ? locations.filter((loc) => chargeLegs.includes(loc.type_of_loc)) : locations;

  const extendedLocations = shouldShowLinehaul 
    ? [...chargeLocations, { location_id: "LINEHAUL", type_of_loc: "LINEHAUL", distance: 0 }]
    : chargeLocations;

  const quoteDetails = orderResponse?.customer_order_details;
  const quoteOrderNumber = quoteDetails?.quote_order_number;
  const quoteAmount = quoteDetails?.quote_amount || 0;
  const quoteRevisions = currentQuote?.quote?.quotes || [];
  const transportationquotecharges = Number(quoteRevisions[quoteRevisions.length -1]?.transportation_charge) || 0;
  const fuelquotecharges = Number(quoteRevisions[quoteRevisions.length -1]?.fuel_surcharge) || 0;
  const accessorialquotecharges = Number(quoteRevisions[quoteRevisions.length -1]?.accessorial_charges) || 0;
  const additionalquotecharges = Number(quoteRevisions[quoteRevisions.length -1]?.additional_charges) || 0;
  
  const handleOpenModal = (locationId, locationType) => {
    setSelectedLocationId(locationId);
    setSelectedLocationType(locationType);
    setIsModalVisible(true);
  };

  useEffect(() => {
    fetchAdjustments();
    fetchRateDetails();
    getQuoteDetails(orderResponse.customer_order_details?.quote_id);
  
  }, [orderResponse]);

  const fetchAdjustments = async () => {
    if (!orderResponse?.customer_order_details?.id) return;
    const orderId = orderResponse.customer_order_details.id;
    const OrgId = orderResponse.customer_order_details.organization_id;
    setAdjustmentLoading(true);
    try {
      const result = await getAdjustments({ order_id: orderId, organization_id: OrgId });

      if (result.success && Array.isArray(result.adjustment_lines)) {
        const locationMap = {};
        const categorizedAdjustments = result.adjustment_lines.reduce((acc, item) => {
          if (!acc[item.location_id]) acc[item.location_id] = [];
          acc[item.location_id].push({
            key: item.id,
            description: item.invoice_name || "N/A",
            gl_code: item.gl_code || "N/A",
            amount: item.invoice_value || 0,
            date: item.created_at ? moment(item.created_at).format(AppConfig.dateFormat) : '',
            quantity: item.invoice_quantity || 0,
            location_id: item.location_id || "N/A",
            apply_driver_pay_for_adjustment: item.apply_driver_pay_for_adjustment
          });
          return acc;
        }, {});
        
        setAdjustmentData(categorizedAdjustments);
        
      } else {
        setAdjustmentData([]);
      }
    } catch (error) {
    } finally {
      setAdjustmentLoading(false);
    }
  };

  const fetchRateDetails = () => {
    if (!orderResponse?.locations?.length) return;
  
    const updatedRateDetails = {};
    orderResponse.locations.forEach((loc) => {
      const quote = orderResponse?.order_quotations?.find(
        (q) => q.location_id === loc.location_id
      );

      updatedRateDetails[loc.location_id] = {
        distance: loc.distance?.toFixed(2) || "0.00",
        rate: quote?.amount != null ? quote.amount.toString() : "",  // 👈 Place this line here
        includesFuel: quote?.configurations?.include_fuel_surcharge === "true",
        name: quote?.name ?? "Transportation Charges",
        flat_fuel_amount: quote?.flat_fuel_amount || "",
      };
    });
    
    // Add linehaul rate details if needed
    if (shouldShowLinehaul) {
      const linehaulQuote = orderResponse?.order_quotations?.find(
        (q) => q.segment_type === "LINEHAUL"
      );
      
      updatedRateDetails["LINEHAUL"] = {
        distance: "0.00",
        rate: linehaulQuote?.amount != null ? linehaulQuote.amount.toString() : "",
        includesFuel: linehaulQuote?.configurations?.include_fuel_surcharge === "true",
        name: linehaulQuote?.name ?? "Linehaul Charges",
        flat_fuel_amount: linehaulQuote?.flat_fuel_amount || "",
      };
    }
  
    setRateDetails(updatedRateDetails);
    setCancelRateDetails(updatedRateDetails);
  };

  const getQuoteDetails = (quote_id) => {
    try {
      QuotesApis.fetchone(quote_id).then((result) => {
        if (result.success) {
          const quoteDetails = result.data || {};
          setCurrentQuote(quoteDetails);
        } else {
          renderAlertMessage(result.errors);
        }
      });
    } catch (error) {
      alertMessage(error.message, "error", 10);
    }
  };
  
  const handleSaveAdjustment = async (e) => {
    e.preventDefault();

    validateFields(async (err, values) => {
      if (!err) {
        setAdjustmentLoading(true); 
        const segmentType = selectedLocationId === "LINEHAUL" ? "LINEHAUL" : orderType;
        
        const commonInvoiceLine = {
          invoice_name: values.description,
          invoice_value: parseFloat(values.amount),
          invoice_quantity: parseInt(values.quantity, 10),
          gl_code: values.glCode,
          invoice_type: "Adjustments",
          location_id: selectedLocationId,
          order_id: orderResponse?.customer_order_details?.id,
          location_type: selectedLocationType,
          apply_driver_pay_for_adjustment: values.apply_driver_pay_for_adjustment,
          segment_type: segmentType,
        };

        const payload = {
          new_invoice_lines: [commonInvoiceLine],
          customer_order_id: orderResponse?.customer_order_details?.id,
          status: "NEW",
          action_from: "ORDER",
          operation_code: "ESTMA",
          current_role: "admin",
          org_id: orderResponse?.customer_order_details?.organization_id,
        };

        if (isEditingAdjustment) {
          payload.invoice_line_id = isEditingAdjustment.key;
        }

        try {
          const result = isEditingAdjustment
            ? await saveEditAdjustment(payload)
            : await saveAddAdjustment(payload);

          if (result.success) {
            form.resetFields();
            setIsModalVisible(false);
            setIsEditingAdjustment(null);
            fetchAdjustments();
            setEstimateRefreshKey(randomNumber());
            if (refreshDetails) {
              refreshDetails();
            }
          } else {
            console.error("Error saving adjustment:", result.errors);
          }
        } catch (error) {
          console.error("Error saving adjustment:", error);
        }
        finally {
          setAdjustmentLoading(false);
        }
      }
    });
  };

  const handleDeleteAdjustment = async (record) => {
    try {
      const payload = {
        invoice_line_id: record.key,
        org_id: orderResponse?.customer_order_details?.organization_id,
        operation_code: "ESTDINVI",
      };
      setAdjustmentLoading(true)
      const result = await deleteAdjustment(payload);

      if (result.success) {
        fetchAdjustments();
        setEstimateRefreshKey(randomNumber()); // Trigger re-fetch of adjustments
        if (refreshDetails) {
          refreshDetails();
        }
      } else {
        console.error("Error deleting adjustment:", result.errors);
      }
    } catch (error) {
      console.error("Error deleting adjustment:", error);
    }  finally {
      setAdjustmentLoading(false);
    }
  };

  const handleEditAdjustment = (record) => {
    setIsEditingAdjustment(record);
    setSelectedLocationId(record.location_id);
    
    // Handle linehaul location type
    let locType = "";
    if (record.location_id === "LINEHAUL") {
      locType = "LINEHAUL";
    } else {
      locType = orderResponse?.locations.find(loc => loc.location_id === record.location_id)?.type_of_loc || "";
    }
    setSelectedLocationType(locType);
    
    setIsModalVisible(true);
    form.setFieldsValue({
      description: record.description,
      amount: record.amount,
      quantity: record.quantity,
      glCode: record.gl_code,
      apply_driver_pay_for_adjustment: record.apply_driver_pay_for_adjustment
    });
  };
  

  const columns = [
    { title: "Description", dataIndex: "description", key: "description" },
    { title: "GL Code", dataIndex: "gl_code", key: "gl_code" },
    { title: "Amount", dataIndex: "amount", key: "amount" },
    { title: "Qty", dataIndex: "quantity", key: "quantity" },
    { title: "Date", dataIndex: "date", key: "date" },
    { title: "Driver Pay", dataIndex: "apply_driver_pay_for_adjustment", key: "apply_driver_pay_for_adjustment",
      render: (text) => (text ? "Yes" : "No"),
      width: 150
    },
    ...(canEdit ? [{
      title: "Action",
      key: "action",
      render: (_, record, index) => (
        <div style={{ display: "flex", gap: "8px" }}>
          <Button data-testid={`adjustment-edit-${index}-button`} type="link" onClick={() => handleEditAdjustment(record)} className="icon-wrapper">
            <EditIconSvg />
          </Button>
          <Button data-testid={`adjustment-delete-${index}-button`} type="link" onClick={() => handleDeleteAdjustment(record)} className="icon-wrapper">
            <DeleteIconAdjustments />
          </Button>
        </div>

      ),
    }] : []),
  ];

  const handleEditCharge = (locationId) => {
    setEditingCharges((prev) => ({
      ...prev,
      [locationId]: true,
    }));
  };

  const handleCancelChargeEdit = (locationId) => {
    setRateDetails((prev) => ({
      ...prev,
      [locationId]: { ...cancelRateDetails[locationId] },
    }));

    setEditingCharges((prev) => ({
      ...prev,
      [locationId]: false,
    }));
};

  
  const handleSaveCharge = (locationId) => {
    const order_id = orderResponse?.customer_order_details?.id;
    const segmentType = locationId === "LINEHAUL" ? "LINEHAUL" : orderType;

    const updatedCharge = {
      customer_order_id: order_id,
      amount: rateDetails[locationId]?.rate || "",
      configurations: {
        include_fuel_surcharge: String(rateDetails[locationId]?.includesFuel || false),
      },
      location_id: locationId,
      name: rateDetails[locationId]?.name || "",
      flat_fuel_amount: rateDetails[locationId]?.flat_fuel_amount || "",
      segment_type: segmentType,
    };

    const payload = {
      order_quotations: [updatedCharge],
      organization_id: orderResponse?.customer_order_details?.organization_id,
      operation_code: "COQA",
      current_role: "admin",
      org_id: orderResponse?.customer_order_details?.organization_id,
    };
    setLoading(true);
    createOrderQuoteAmount(payload)
      .then((data) => {
        if (data.success) {
          renderNotification("Charges Saved Successfully", "success");
          setCancelRateDetails((prev) => ({
            ...prev,
            [locationId]: {
              ...prev[locationId],
              rate: rateDetails[locationId]?.rate,
              includesFuel: rateDetails[locationId]?.includesFuel,
              name: rateDetails[locationId]?.name || "",
              flat_fuel_amount: rateDetails[locationId]?.flat_fuel_amount || "",
            },
          }));
          setEditingCharges((prev) => ({
            ...prev,
            [locationId]: false,
          }));
          setEstimateRefreshKey(randomNumber());
          if (refreshDetails) {
            refreshDetails();
          }
        } else if (data.errors) {
          renderAlertMessage(data.errors);
        }
      })
      .catch((error) => {
        console.error("Error while saving charge:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  
  const handleSubmit = (e) => {
    e.preventDefault();
    validateFields((err) => {
      if (!err) {
        form.resetFields();
        setIsModalVisible(false);
      }
    });
  };

  const hasAnyLocationWithAmountAndFuel = Object.values(rateDetails).some((loc) => !isEmpty(loc.rate) && !isEmpty(loc.flat_fuel_amount));

  const hasAnyLocationWithAmountAndIncludesFuel = Object.values(rateDetails).some((loc) => !isEmpty(loc.rate) && loc.includesFuel);
  
  const renderCharges = (location, index) => {
    const isEditing = editingCharges[location.location_id] || false;
    const locationType = location.type_of_loc;
    const orderType = orderResponse?.customer_order_details?.type_of_order
    const chargeData = orderResponse?.order_quotations?.find(
      (quote) => quote.location_id === location.location_id
    );
    const distanceValue = location.distance || "0";
    const locationRate = rateDetails[location.location_id] || {};
    const Name = locationRate.name;
    const rateValue = locationRate.rate;
    const fuelValue = locationRate.flat_fuel_amount;
    const includesFuel = locationRate.includesFuel;
    
    let sectionTitle = "";
    if (locationType === "LINEHAUL") {
      sectionTitle = "Linehaul Charges";
    } else if (orderType === "MS") {
      sectionTitle = `Stop${index + 1} (${locationType === "PICKUP" ? "Pickup" : "Delivery"}) Charges`;
    } else {
      sectionTitle = locationType === "PICKUP" ? "Pickup Charges" : "Delivery Charges";
    }
    
    return (
      <Col data-testid={`${sectionTitle.replace(/\s+/g, '')}-section`} span={extendedLocations.length === 1 ? 24 : 12} key={`charges-${index}`}>
        <Spin spinning={loading}>
        <SectionCard
            title={sectionTitle}
          extraActionNode={
            !canEdit ? null : isEditing ? (
              <>
                <Button data-testid="cancel-charges-button"  disabled={loading} type="link"  className="ButtonStyle" onClick={() => handleCancelChargeEdit(location.location_id, location.type_of_loc)} style={{ marginRight: "8px" }}>Cancel</Button>
                <Button data-testid="save-charges-button" disabled={loading} loading={loading} type="link"  className="ButtonStyleSave" onClick={() => handleSaveCharge(location.location_id, location.type_of_loc)}>Save</Button>
              </>
            ) : (
              <Button data-testid="edit-charges-button" type="link"  className="ButtonStyle" onClick={() => handleEditCharge(location.location_id)}>Edit</Button>
            )
          }
            bodyStyle={{ margin: "0px 0px 0px" }}
          >
          {!isEditing ? (
              <>
                <Row>
                  <Col>
                    <span className="ChargesFieldsNoEditNameStyle">Name</span>
                    <span className="ChargesFieldsNoEditStyle">{Name}</span>
                  </Col>
                </Row>
           <Row className="ChargesDisplay" data-testid="charges-details">
            <Col span={6}>
             <span className="ChargesFieldsNoEditNameStyle">Distance</span>
                  <span className="ChargesFieldsNoEditStyle">{parseFloat(distanceValue).toFixed(2)} mi</span>
           </Col>
            <Col span={6}>
             <span className="ChargesFieldsNoEditNameStyle">Amount</span>
                  <span className="ChargesFieldsNoEditStyle">
                    {rateValue ? `$ ${parseFloat(rateValue).toFixed(2)}` : "Not Available"}
                  </span>
           </Col>
            <Col span={6}>
                  <span className="ChargesFieldsNoEditNameStyle">Fuel</span>
                  <span className="ChargesFieldsNoEditStyle">
                    {fuelValue ? `$ ${parseFloat(fuelValue).toFixed(2)}` : "Not Available"}
                  </span>
            </Col>
            <Col span={6}>
             <span className="ChargesFieldsNoEditNameStyle">Includes Fuel</span>
                  <span className="ChargesFieldsNoEditStyle">
                    {includesFuel ? "Yes" : "No"}
                  </span>
            </Col>
         </Row>
              </>

          ) : (
                <>
                  <Row>
                    <Col>
                      <span className="ModalFieldStyle">Name</span>
                      <Input
                        data-testid="name-edit-input"
                        className="ChargesFieldsStyle"
                        value={rateDetails[location.location_id]?.name}
                        type="text"
                        onChange={(event) => {
                          const value = event.target.value;
                          setRateDetails((prev) => ({
                            ...prev,
                            [location.location_id]: { ...prev[location.location_id], name: value },
                          }));
                        }}
                      />
                    </Col>
                  </Row>
            <Row gutter={[16, 16]} className="ChargesFieldsContainer antdStyles" data-testid="charges-details-edit">
            <Col span={6}>
              <span className="ModalFieldStyle">Distance</span>
                  <Input
                    data-testid="distance-input"
                    disabled
                    suffix={<span className="PrefixStyle">mi</span>}
                    className="ChargesFieldsStyle"
                        value={location.distance}
                        type="text"
                    onChange={(event) => {
                      const value = event.target.value;
                        setRateDetails((prev) => ({
                          ...prev,
                          [locationType]: { ...prev[locationType], name: value },
                        }));
                    }}
                  />
              </Col>
            <Col span={6}>
                <span className="ModalFieldStyle">Amount</span>
                  <Input
                    data-testid="amount-input"
                    prefix={<span className="PrefixStyle">$</span>}
                    className="ChargesFieldsStyle"
                      value={rateDetails[location.location_id]?.rate ?? ""}
                    type="text"
                    onKeyPress={(event) => {
                      if (!/[\d.\-]/.test(event.key)) {
                        event.preventDefault();
                      }
                    }}
                    onChange={(event) => {
                      const value = event.target.value;
                      if(event.target.value==""){
                        setRateDetails((prev) => ({
                          ...prev,
                          [location.location_id]: { ...prev[location.location_id], rate: value,flat_fuel_amount: "", includesFuel: false },
                        }));
                      }else if (/^-?\d*\.?\d*$/.test(value)) {
                        setRateDetails((prev) => ({
                          ...prev,
                          [location.location_id]: { ...prev[location.location_id], rate: value },
                        }));
                      }
                    }}
                  />
              </Col>
            <Col span={6}>
                    <span className="ModalFieldStyle">Fuel</span>
                    <Input
                      data-testid="fuel-input"
                      prefix={<span className="PrefixStyle">$</span>}
                      className="ChargesFieldsStyle"
                      value={rateDetails[location.location_id]?.flat_fuel_amount ?? ""}
                      type="text"
                      disabled={isEmpty(rateDetails[location.location_id]?.rate) || rateDetails[location.location_id]?.includesFuel || (hasAnyLocationWithAmountAndIncludesFuel && !isEmpty(rateDetails[location.location_id]?.rate))}                      
                      onKeyPress={(event) => {
                        if (!/[\d.\-]/.test(event.key)) {
                          event.preventDefault();
                        }
                      }}
                      onChange={(event) => {
                        const value = event.target.value;
                        if (/^-?\d*\.?\d*$/.test(value)) {
                          setRateDetails((prev) => ({
                            ...prev,
                            [location.location_id]: { ...prev[location.location_id], flat_fuel_amount: value },
                          }));
                        }
                      }}
                    />
            </Col>
            <Col span={6} className="ChargesFieldsContainer">
                  <Checkbox
                    data-testid="include-fuel-checkbox"
                    style={{paddingTop:"17px"}}
                    className={`ChargesFieldsStyle ${rateDetails.includesFuel ? "checked" : ""}`}         
                    checked={rateDetails[location.location_id]?.includesFuel}
                    disabled={isEmpty(rateDetails[location.location_id]?.rate) || !isEmpty(rateDetails[location.location_id]?.flat_fuel_amount) || (hasAnyLocationWithAmountAndFuel && !isEmpty(rateDetails[location.location_id]?.rate))}                    
                    onChange={(e) =>
                      setRateDetails((prev) => ({
                        ...prev,
                        [location.location_id]: { ...prev[location.location_id], includesFuel: e.target.checked },
                      }))
                    }
                  >
                    Includes Fuel
                  </Checkbox>
                </Col>
            </Row>
                </>          
          )}
        </SectionCard>
        </Spin>
      </Col>
    );
  };

  return (
    <>
    <div className="adjustments-scroll-container">
      <Row gutter={[16, 16]} type="flex" align="top" justify="start">
        {extendedLocations.map((location, index) => renderCharges(location, index))}
      </Row>
      { quoteOrderNumber && (
      <Row style={{ marginTop: "16px", marginBottom: "16px" }}>
        <SectionCard
          title={<div style={{display:"flex"}}>
            <span>Original Quote</span>
          <span    c
            style={{ 
              color: "black", 
              cursor: "pointer", 
              transition: "color 0.3s ease" 
            }}
            onMouseOver={(e) => e.target.style.color = "#586FCC"}
            onMouseOut={(e) => e.target.style.color = "black"}
            onClick={() => {setViewTab("quote"), setShowDrawer(true)}}
          >{`(#${quoteOrderNumber})`}</span>
          </div>
          }
          bodyStyle={{ margin: "0px 0px 0px" }}
          extraActionNode={
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                cursor: "pointer",
              }}
              onClick={() => setCollapsed(!collapsed)}
            >
              <span style={{ fontWeight: 600, fontSize: "16px" }}>Quote Total: ${quoteAmount.toFixed(2)}</span>

              <span className="icon-wrapper">
                {collapsed ? <ChevrondownIcon /> : <ChevronUpIcon />}
              </span>
            </div>

          }
        >
          {!collapsed && (
            <div className="quote-section">
                {[
                  { label: "Transportation Charges", amount: `${checkNegitive(transportationquotecharges)}` },
                  { label: "Fuel Charges", amount: `${checkNegitive(fuelquotecharges)}` },
                  { label: "Accessorial Charges", amount: `${checkNegitive(accessorialquotecharges)}` },
                  { label: "Additional Charges", amount: `${checkNegitive(additionalquotecharges)}` },
                  { label: "Total", amount: `${checkNegitive(quoteAmount)}`, bold: true },
                ].map(({ label, amount, bold }) => (
                <div key={label} className="quote-row">
                  <span className={`text-style ${bold ? "bold" : ""}`}>{label}</span>
                  <span className={`text-style ${bold ? "bold" : ""}`}>{amount}</span>
                </div>
              ))}
            </div>

          )}
        </SectionCard>
      </Row>
      )}

      <Row gutter={[16, 16]} type="flex" align="top" justify="start">
        {extendedLocations?.length > 0 && (() => {
          const availableLocations = extendedLocations || [];
          // ["PICKUP", "DELIVERY"].filter(type =>
          //   orderResponse.locations.some(loc => loc.type_of_loc === type)
          // );
          const typeOfOrder = orderResponse.type_of_order;
          return availableLocations.map((location, index) => {
            const type = location.type_of_loc || 'DELIVERY'
            // const location = orderResponse.locations.find(loc => loc.type_of_loc === type);
            const subtotal = (adjustmentData[location.location_id] || []).reduce((sum, item) => {
              const amount = parseFloat(item.amount) || 0;
              return sum + amount;
            }, 0);
            
            let adjustmentTitle = "";
            if (type === "LINEHAUL") {
              adjustmentTitle = "Linehaul Adjustments";
            } else if (typeOfOrder === 'MS') {
              adjustmentTitle = `Stop ${index + 1} (${type}) Adjustments`;
            } else {
              adjustmentTitle = `${type} Adjustments`;
            }
            
          return (
            <Col data-testid={`${adjustmentTitle.replace(/\s+/g, '')}-section`} span={availableLocations.length === 1 ? 24 : 12} key={`adjustment-${location.location_id}`}>
              <Spin spinning={adjustmentLoading}>
              <SectionCard
                  title={adjustmentTitle}
                extraActionNode={
                  canEdit ? (
                  <Button
                    data-testid="add-adjustments-button"
                    type="link"
                    className="ButtonStyle"
                    onClick={() => {
                      if (location) {
                        handleOpenModal(location.location_id, location.type_of_loc);
                      } else {
                        console.error(`No location found for type: ${type}`);
                      }
                    }}
                  >
                    Add Adjustment
                  </Button>
                  ) : null
                }
              >
                
                <Table data-testid="adjustment-list" columns={columns.map(col => ({
                  ...col,
                  onCell: () => ({
                    className: "tableDataStyle"
                  })
                }))} dataSource={adjustmentData[location.location_id] || []} pagination={false} locale={{
                  emptyText: <div style={{ padding: "4px" }}>No Data</div>,
                }} />
                  {adjustmentData[location.location_id] && adjustmentData[location.location_id].length > 0 && (
                  <Row style={{ height: "40px", display: "flex", justifyContent: "center", alignItems: "center" }}>
                    <Col className="subtotal-style">
                     Sub Total &nbsp; &nbsp; {subtotal > 0 ? `$${subtotal.toFixed(2)}` : ""}
                    </Col>
                  </Row>
                )}

              </SectionCard>
              </Spin>
            </Col>
          );
        });
      })()}
    </Row>
    </div>

      <Modal  
      className="antdStyles"
        closable={false}
        title={isEditingAdjustment ? "Edit Adjustment" : "Add Adjustment"}
        visible={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setIsEditingAdjustment(null);
          setSelectedLocationId(null);
          setSelectedLocationType(null); 
          form.resetFields();
        }}
        footer={[
          <Button data-testid="cancel-adjustment-button"  disabled={loading} className="CancelButton" key="cancel" type="link" onClick={() => {
            setIsModalVisible(false);
            setIsEditingAdjustment(null);
            setSelectedLocationId(null); 
            setSelectedLocationType(null);
            form.resetFields();
          }}>
            Cancel
          </Button>,
          <Button data-testid="save-adjustment-button"  disabled={adjustmentLoading}
          loading={adjustmentLoading} 
          className="ButtonStyleSave" key="submit" type="primary" onClick={handleSaveAdjustment}>
            {isEditingAdjustment ? "Update Adjustment" : "Add Adjustment"}
          </Button>,
        ]}
      >
        <Form layout="vertical" onSubmit={handleSubmit} className="adjustmentForm" data-testid="adjustments-modal">
          <Row>
            <Col span={24} className=" alignLeft">
              <Form.Item>
                {getFieldDecorator("apply_driver_pay_for_adjustment", {
                  valuePropName: 'checked',
                })(
                  <Checkbox
                  data-testid="eligible-checkbox"
                  >
                    {I18n.t('accessorials.eligible_for_driver_pay')}
                  </Checkbox>
                )}
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Form.Item label={<span className="ModalFieldStyle ">Description</span>}>
                {getFieldDecorator("description", {
                  rules: [{ required: true, message: "Description is required!" }],
                })(<Input data-testid="description-input" className="AntdInputField PlaceholderStyle" placeholder="Enter Description" />)}
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Form.Item label={<span className="ModalFieldStyle">Total Amount</span>}>
                {getFieldDecorator("amount", {
                  rules: [{ required: true, message: "Amount is required!" }],
                })(
                  <Input
                    data-testid="total-amount-input"
                    prefix={<span className="PrefixStyle">$</span>}
                    className="AntdInputField PlaceholderStyle"
                    placeholder="Enter Amount"
                    type="text"
                    onKeyPress={(event) => {
                      if (!/[\d.-]/.test(event.key)) {
                        event.preventDefault();
                      }
                    }}
                    onChange={(event) => {
                      const value = event.target.value;
                      if (!/^-?\d*\.?\d*$/.test(value)) {
                        event.preventDefault();
                      }
                    }}
                  />
                )}
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label={<span className="ModalFieldStyle">Quantity</span>}>
                {getFieldDecorator("quantity", {
                  rules: [{ required: true, message: "Quantity is required!" }],
                })(
                  <Input
                    data-testid="quantity-input"
                    className="AntdInputField PlaceholderStyle"
                    placeholder="Enter Quantity"
                    type="number"
                    onKeyPress={(event) => {
                      if (!/[0-9]/.test(event.key)) {
                        event.preventDefault();
                      }
                    }}
                  />
                )}
              </Form.Item>
            </Col>

          </Row>
          < Row gutter={[16, 16]}>
            <Col span={12}>
              <Form.Item label={<span className="ModalFieldStyle">GL Code</span>}>
                {getFieldDecorator("glCode", {
                  rules: [{ required: true, message: "GL Code is required!" }],
                })(
                  <GLCodeSelect
                    data-testid="gl-code-input"
                    data={glCodesList || []}
                    style={{ width: 120 }}
                    showSearch
                    placeholder={I18n.t("invoices.gl_code")}
                    dropdownMatchSelectWidth={false}
                    value={form.getFieldValue("gl_code")}
                    // onChange={(value) =>
                    //   this.handleInputChange(value, index, "gl_code")
                    // }
                    selectKey={"gl_code"}
                    selectValue={"gl_code"}
                    selectedValue={"gl_code"}
                    selectedCode={"description"}
                    styleObj={{  width: '100%' }}
                    size="default"
                  />
                )}
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* Quote Order Deatils Drawer */}
      <Drawer
        visible={showDrawer}
        placement="right"
        closable={false}
        maskClosable={false}
        width={1000}
        className="order-details-drawer hide-scrollbar"
      >
        <OrderDetailsDrawer
          viewTab={viewTab}
          setShowDrawer={setShowDrawer}
          showDrawer={showDrawer}
          orderResponse={orderResponse}
          refreshDetails={refreshDetails}
          account={account}
        />
      </Drawer>
    </>
  );
};

export default withRouter(Form.create()(Adjustments));